import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import ClosureNotice from './ClosureNotice';

const Header = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServiceAreaOpen, setIsServiceAreaOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [servicesHoverTimeout, setServicesHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [pricingHoverTimeout, setPricingHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [aboutHoverTimeout, setAboutHoverTimeout] = useState<NodeJS.Timeout | null>(null);

  // Close all dropdowns when a link is clicked
  const handleLinkClick = () => {
    setIsServiceAreaOpen(false);
    setIsServicesOpen(false);
    setIsPricingOpen(false);
    setIsAboutOpen(false);
    // Clear any pending timeouts
    if (hoverTimeout) clearTimeout(hoverTimeout);
    if (servicesHoverTimeout) clearTimeout(servicesHoverTimeout);
    if (pricingHoverTimeout) clearTimeout(pricingHoverTimeout);
    if (aboutHoverTimeout) clearTimeout(aboutHoverTimeout);
  };

  // Smart link generation based on current page
  const getNavigationLink = (section: string) => {
    if (location.pathname === '/') {
      return `#${section}`; // Anchor for main page
    } else {
      return `/#${section}`; // Route to main + anchor
    }
  };

  // Check if we're on a services page
  const isOnServicesPage = () => {
    return ['/residential-plumbing', '/commercial-plumbing', '/sewer-repair-replacement', '/bathroom-kitchen-remodel', '/custom-home-plumbing'].includes(location.pathname);
  };

  // Check if we're on a service areas page
  const isOnServiceAreasPage = () => {
    return ['/highlands-ranch-plumber', '/littleton-plumber', '/lakewood-plumber', '/thornton-plumber', '/denver-plumber', '/arvada-plumber', '/castle-rock-plumber', '/centennial-plumber'].includes(location.pathname);
  };

  // Check if we're on a pricing page
  const isOnPricingPage = () => {
    return ['/pricing', '/water-heater-pricing', '/water-heater-summer-special', '/tankless-water-heater-pricing', '/sewer-repair-pricing', '/sewer-cleaning-pricing', '/sewer-replacement-spot-repair-pricing', '/diagnostic-services-pricing', '/garbage-disposal-pricing', '/faucet-pricing'].includes(location.pathname);
  };

  // Check if we're on an about page
  const isOnAboutPage = () => {
    return ['/meet-the-team', '/team/codee-jessen', '/team/michelle-jessen', '/team/zack', '/team/jesse-sellers'].includes(location.pathname);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleMouseEnter = () => {
    // Clear any pending timeout when entering button or dropdown area
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    if (servicesHoverTimeout) {
      clearTimeout(servicesHoverTimeout);
    }
    if (pricingHoverTimeout) {
      clearTimeout(pricingHoverTimeout);
    }
    if (aboutHoverTimeout) {
      clearTimeout(aboutHoverTimeout);
    }
    setIsServicesOpen(false); // Close Services dropdown
    setIsPricingOpen(false); // Close Pricing dropdown
    setIsAboutOpen(false); // Close About dropdown
    setIsServiceAreaOpen(true);
  };

  const handleMouseLeave = () => {
    // Extended delay based on UX research - gives users time to move to dropdown
    const timeout = setTimeout(() => {
      setIsServiceAreaOpen(false);
    }, 1600); // Doubled grace period for better user experience
    setHoverTimeout(timeout);
  };

  // Handler for when mouse enters the dropdown itself - clears any pending timeout
  const handleDropdownEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  // Handler for when mouse leaves the dropdown itself
  const handleDropdownLeave = () => {
    // Immediate close when actually leaving dropdown - simplified approach
    setIsServiceAreaOpen(false);
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  const handleServicesMouseEnter = () => {
    // Clear any pending timeout when entering button or dropdown area
    if (servicesHoverTimeout) {
      clearTimeout(servicesHoverTimeout);
      setServicesHoverTimeout(null);
    }
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    if (pricingHoverTimeout) {
      clearTimeout(pricingHoverTimeout);
    }
    if (aboutHoverTimeout) {
      clearTimeout(aboutHoverTimeout);
    }
    setIsServiceAreaOpen(false); // Close Service Areas dropdown
    setIsPricingOpen(false); // Close Pricing dropdown
    setIsAboutOpen(false); // Close About dropdown
    setIsServicesOpen(true);
  };

  const handleServicesMouseLeave = () => {
    // Extended delay based on UX research - gives users time to move to dropdown
    const timeout = setTimeout(() => {
      setIsServicesOpen(false);
    }, 1600); // Doubled grace period for better user experience
    setServicesHoverTimeout(timeout);
  };

  // Handler for when mouse enters the services dropdown itself
  const handleServicesDropdownEnter = () => {
    if (servicesHoverTimeout) {
      clearTimeout(servicesHoverTimeout);
      setServicesHoverTimeout(null);
    }
  };

  // Handler for when mouse leaves the services dropdown itself
  const handleServicesDropdownLeave = () => {
    // Immediate close when actually leaving dropdown - simplified approach
    setIsServicesOpen(false);
    if (servicesHoverTimeout) {
      clearTimeout(servicesHoverTimeout);
      setServicesHoverTimeout(null);
    }
  };

  const handlePricingMouseEnter = () => {
    // Clear any pending timeout when entering button or dropdown area
    if (pricingHoverTimeout) {
      clearTimeout(pricingHoverTimeout);
      setPricingHoverTimeout(null);
    }
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    if (servicesHoverTimeout) {
      clearTimeout(servicesHoverTimeout);
    }
    if (aboutHoverTimeout) {
      clearTimeout(aboutHoverTimeout);
    }
    setIsServiceAreaOpen(false); // Close Service Areas dropdown
    setIsServicesOpen(false); // Close Services dropdown
    setIsAboutOpen(false); // Close About dropdown
    setIsPricingOpen(true);
  };

  const handlePricingMouseLeave = () => {
    // Extended delay based on UX research - gives users time to move to dropdown
    const timeout = setTimeout(() => {
      setIsPricingOpen(false);
    }, 1600); // Doubled grace period for better user experience
    setPricingHoverTimeout(timeout);
  };

  // Handler for when mouse enters the pricing dropdown itself
  const handlePricingDropdownEnter = () => {
    if (pricingHoverTimeout) {
      clearTimeout(pricingHoverTimeout);
      setPricingHoverTimeout(null);
    }
  };

  // Handler for when mouse leaves the pricing dropdown itself
  const handlePricingDropdownLeave = () => {
    // Immediate close when actually leaving dropdown - simplified approach
    setIsPricingOpen(false);
    if (pricingHoverTimeout) {
      clearTimeout(pricingHoverTimeout);
      setPricingHoverTimeout(null);
    }
  };

  const handleAboutMouseEnter = () => {
    if (aboutHoverTimeout) {
      clearTimeout(aboutHoverTimeout);
    }
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    if (servicesHoverTimeout) {
      clearTimeout(servicesHoverTimeout);
    }
    if (pricingHoverTimeout) {
      clearTimeout(pricingHoverTimeout);
    }
    setIsServiceAreaOpen(false); // Close Service Areas dropdown
    setIsServicesOpen(false); // Close Services dropdown
    setIsPricingOpen(false); // Close Pricing dropdown
    setIsAboutOpen(true);
  };

  const handleAboutMouseLeave = () => {
    // Extended delay based on UX research - gives users time to move to dropdown
    const timeout = setTimeout(() => {
      setIsAboutOpen(false);
    }, 1600); // Doubled grace period for better user experience
    setAboutHoverTimeout(timeout);
  };

  // Handler for when mouse enters the about dropdown itself
  const handleAboutDropdownEnter = () => {
    if (aboutHoverTimeout) {
      clearTimeout(aboutHoverTimeout);
      setAboutHoverTimeout(null);
    }
  };

  // Handler for when mouse leaves the about dropdown itself
  const handleAboutDropdownLeave = () => {
    // Immediate close when actually leaving dropdown - simplified approach
    setIsAboutOpen(false);
    if (aboutHoverTimeout) {
      clearTimeout(aboutHoverTimeout);
      setAboutHoverTimeout(null);
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes subtle-glow {
            0%, 100% {
              box-shadow: 0 0 20px rgba(5, 150, 105, 0.3), 0 4px 14px 0 rgba(0, 0, 0, 0.1);
            }
            50% {
              box-shadow: 0 0 30px rgba(5, 150, 105, 0.6), 0 6px 20px 0 rgba(0, 0, 0, 0.15);
            }
          }
        `}
      </style>
      
      <ClosureNotice />

      <header 
        className={cn(
          "fixed w-full z-40 transition-all duration-300 ease-in-out py-4", 
          "top-10", // Adjusted for top bar
          isScrolled 
            ? "bg-gradient-to-r from-trust-deep to-authority-navy shadow-2xl" 
            : "bg-gradient-to-r from-trust-deep/95 to-authority-navy/95 backdrop-blur-md shadow-xl"
        )}
      >
      <div className="container-custom flex justify-between items-center">
        <div className="flex items-center flex-shrink-0">
          <a href="/" className="bg-neutral-white rounded-2xl p-4 md:p-5 lg:p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] border border-neutral-border/20">
            <img 
              src="/colorado-plumbing-uploads/coloradofirst_logo_best_plumber.png" 
              alt="Colorado First Plumbing and Sewer LLC Logo" 
              className="h-20 md:h-24 lg:h-28 w-auto"
              fetchPriority="high"
            />
          </a>
        </div>
        
        <nav className="hidden md:flex items-center space-x-2 lg:space-x-3 ml-4 lg:ml-10 flex-1 justify-center">
          <a href="/" className="px-3 lg:px-4 py-2 rounded-lg bg-gradient-to-r from-action-primary to-action-urgent text-white hover:from-action-urgent hover:to-red-700 transition-all duration-300 text-sm font-medium shadow-md hover:shadow-xl border border-action-primary hover:border-action-urgent transform hover:scale-[1.02]">Home</a>
          
          {/* Services Dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsServicesOpen(!isServicesOpen)}
              onMouseEnter={handleServicesMouseEnter}
              onMouseLeave={handleServicesMouseLeave}
              className={cn(
                "flex items-center px-3 lg:px-4 py-2 rounded-lg bg-gradient-to-r text-white transition-all duration-300 text-sm font-medium shadow-md hover:shadow-xl border transform hover:scale-[1.02]",
                isOnServicesPage() 
                  ? "from-action-urgent to-red-700 border-action-urgent hover:from-red-700 hover:to-red-800 hover:border-red-700" 
                  : "from-action-primary to-action-urgent border-action-primary hover:from-action-urgent hover:to-red-700 hover:border-action-urgent"
              )}
            >
              Services
              <ChevronDown size={16} className="ml-1" />
            </button>
            
            {isServicesOpen && (
              <div 
                className="absolute top-full left-0 mt-0 bg-gradient-to-br from-neutral-white to-trust-light/30 rounded-xl shadow-2xl border border-trust-primary/20 py-3 min-w-64 z-50 backdrop-blur-sm"
                onMouseEnter={handleServicesDropdownEnter}
                onMouseLeave={handleServicesDropdownLeave}
                onClick={handleLinkClick}
              >
                <a 
                  href="/residential-plumbing" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-trust-primary hover:to-trust-deep hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-trust-primary/20 hover:shadow-lg",
                    location.pathname === '/residential-plumbing' 
                      ? "bg-gradient-to-r from-trust-primary to-trust-deep text-white border-trust-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                  onClick={handleLinkClick}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-success-primary rounded-full mr-3"></span>
                    Residential Plumbing
                  </span>
                </a>
                <a 
                  href="/commercial-plumbing" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-trust-primary hover:to-trust-deep hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-trust-primary/20 hover:shadow-lg",
                    location.pathname === '/commercial-plumbing' 
                      ? "bg-gradient-to-r from-trust-primary to-trust-deep text-white border-trust-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                  onClick={handleLinkClick}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-action-primary rounded-full mr-3"></span>
                    Commercial Plumbing
                  </span>
                </a>
                <a 
                  href="/sewer-repair-replacement" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-trust-primary hover:to-trust-deep hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-trust-primary/20 hover:shadow-lg",
                    location.pathname === '/sewer-repair-replacement' 
                      ? "bg-gradient-to-r from-trust-primary to-trust-deep text-white border-trust-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-action-urgent rounded-full mr-3"></span>
                    Sewer Repair & Replacement
                  </span>
                </a>
                <a 
                  href="/bathroom-kitchen-remodel" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-trust-primary hover:to-trust-deep hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-trust-primary/20 hover:shadow-lg",
                    location.pathname === '/bathroom-kitchen-remodel' 
                      ? "bg-gradient-to-r from-trust-primary to-trust-deep text-white border-trust-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-success-eco rounded-full mr-3"></span>
                    Bathroom & Kitchen Remodel
                  </span>
                </a>
                <a
                  href="/custom-home-plumbing"
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-trust-primary hover:to-trust-deep hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-trust-primary/20 hover:shadow-lg",
                    location.pathname === '/custom-home-plumbing' 
                      ? "bg-gradient-to-r from-trust-primary to-trust-deep text-white border-trust-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-success-eco rounded-full mr-3"></span>
                    Custom Home Plumbing
                  </span>
                </a>
              </div>
            )}
          </div>
          
          {/* Service Areas Dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsServiceAreaOpen(!isServiceAreaOpen)}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              className={cn(
                "flex items-center px-3 lg:px-4 py-2 rounded-lg bg-gradient-to-r text-white transition-all duration-300 text-sm font-medium shadow-md hover:shadow-xl border transform hover:scale-[1.02]",
                isOnServiceAreasPage() 
                  ? "from-action-urgent to-red-700 border-action-urgent hover:from-red-700 hover:to-red-800 hover:border-red-700" 
                  : "from-action-primary to-action-urgent border-action-primary hover:from-action-urgent hover:to-red-700 hover:border-action-urgent"
              )}
            >
              Areas
              <ChevronDown size={16} className="ml-1" />
            </button>
            
            {isServiceAreaOpen && (
              <div 
                className="absolute top-full left-0 mt-0 bg-gradient-to-br from-neutral-white to-success-light/30 rounded-xl shadow-2xl border border-success-primary/20 py-3 min-w-60 z-50 backdrop-blur-sm"
                onMouseEnter={handleDropdownEnter}
                onMouseLeave={handleDropdownLeave}
                onClick={handleLinkClick}
              >
                <a 
                  href="/highlands-ranch-plumber" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-success-primary hover:to-success-eco hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-success-primary/20 hover:shadow-lg",
                    location.pathname === '/highlands-ranch-plumber' 
                      ? "bg-gradient-to-r from-success-primary to-success-eco text-white border-success-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-trust-primary rounded-full mr-3"></span>
                    Highlands Ranch Plumber
                  </span>
                </a>
                <a 
                  href="/littleton-plumber" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-success-primary hover:to-success-eco hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-success-primary/20 hover:shadow-lg",
                    location.pathname === '/littleton-plumber' 
                      ? "bg-gradient-to-r from-success-primary to-success-eco text-white border-success-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-action-primary rounded-full mr-3"></span>
                    Littleton Plumber
                  </span>
                </a>
                <a 
                  href="/lakewood-plumber" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-success-primary hover:to-success-eco hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-success-primary/20 hover:shadow-lg",
                    location.pathname === '/lakewood-plumber' 
                      ? "bg-gradient-to-r from-success-primary to-success-eco text-white border-success-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-action-urgent rounded-full mr-3"></span>
                    Lakewood Plumber
                  </span>
                </a>
                <a 
                  href="/longmont-plumber" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-success-primary hover:to-success-eco hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-success-primary/20 hover:shadow-lg",
                    location.pathname === '/longmont-plumber' 
                      ? "bg-gradient-to-r from-success-primary to-success-eco text-white border-success-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-success-eco rounded-full mr-3"></span>
                    Longmont Plumber
                  </span>
                </a>
                <a 
                  href="/thornton-plumber" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-success-primary hover:to-success-eco hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-success-primary/20 hover:shadow-lg",
                    location.pathname === '/thornton-plumber' 
                      ? "bg-gradient-to-r from-success-primary to-success-eco text-white border-success-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-action-warm rounded-full mr-3"></span>
                    Thornton Plumber
                  </span>
                </a>
                <a 
                  href="/denver-plumber" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-success-primary hover:to-success-eco hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-success-primary/20 hover:shadow-lg",
                    location.pathname === '/denver-plumber' 
                      ? "bg-gradient-to-r from-success-primary to-success-eco text-white border-success-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-trust-deep rounded-full mr-3"></span>
                    Denver Plumber
                  </span>
                </a>
                <a 
                  href="/arvada-plumber" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-success-primary hover:to-success-eco hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-success-primary/20 hover:shadow-lg",
                    location.pathname === '/arvada-plumber' 
                      ? "bg-gradient-to-r from-success-primary to-success-eco text-white border-success-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-neutral-gray rounded-full mr-3"></span>
                    Arvada Plumber
                  </span>
                </a>
                <a 
                  href="/castle-rock-plumber" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-success-primary hover:to-success-eco hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-success-primary/20 hover:shadow-lg",
                    location.pathname === '/castle-rock-plumber' 
                      ? "bg-gradient-to-r from-success-primary to-success-eco text-white border-success-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-action-primary rounded-full mr-3"></span>
                    Castle Rock Plumber
                  </span>
                </a>
                <a 
                  href="/centennial-plumber" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-success-primary hover:to-success-eco hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-success-primary/20 hover:shadow-lg",
                    location.pathname === '/centennial-plumber' 
                      ? "bg-gradient-to-r from-success-primary to-success-eco text-white border-success-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-success-primary rounded-full mr-3"></span>
                    Centennial Plumber
                  </span>
                </a>
              </div>
            )}
          </div>
          
          <a 
            href="/reviews" 
            className={cn(
              "px-3 lg:px-4 py-2 rounded-lg bg-gradient-to-r text-white transition-all duration-300 text-sm font-medium shadow-md hover:shadow-xl border transform hover:scale-[1.02]",
              location.pathname === '/reviews' 
                ? "from-action-urgent to-red-700 border-action-urgent hover:from-red-700 hover:to-red-800 hover:border-red-700" 
                : "from-action-primary to-action-urgent border-action-primary hover:from-action-urgent hover:to-red-700 hover:border-action-urgent"
            )}
          >
            Reviews
          </a>
          
          {/* About Dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsAboutOpen(!isAboutOpen)}
              onMouseEnter={handleAboutMouseEnter}
              onMouseLeave={handleAboutMouseLeave}
              className={cn(
                "flex items-center px-3 lg:px-4 py-2 rounded-lg bg-gradient-to-r text-white transition-all duration-300 text-sm font-medium shadow-md hover:shadow-xl border transform hover:scale-[1.02]",
                isOnAboutPage() 
                  ? "from-action-urgent to-red-700 border-action-urgent hover:from-red-700 hover:to-red-800 hover:border-red-700" 
                  : "from-action-primary to-action-urgent border-action-primary hover:from-action-urgent hover:to-red-700 hover:border-action-urgent"
              )}
            >
              About
              <ChevronDown size={16} className="ml-1" />
            </button>
            
            {isAboutOpen && (
              <div 
                className="absolute top-full left-0 mt-0 bg-gradient-to-br from-neutral-white to-action-primary/10 rounded-xl shadow-2xl border border-action-primary/20 py-3 min-w-64 z-50 backdrop-blur-sm"
                onMouseEnter={handleAboutDropdownEnter}
                onMouseLeave={handleAboutDropdownLeave}
                onClick={handleLinkClick}
              >
                <a 
                  href="/meet-the-team" 
                  className="block px-6 py-3 text-sm font-semibold text-authority-navy hover:bg-gradient-to-r hover:from-action-primary hover:to-action-urgent hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-action-primary/20 hover:shadow-lg"
                  onClick={handleLinkClick}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-action-primary rounded-full mr-3"></span>
                    Meet the Team
                  </span>
                </a>
                <div className="border-t border-action-primary/10 my-2 mx-4"></div>
                <a 
                  href="/team/codee-jessen" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-action-primary hover:to-action-urgent hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-action-primary/20 hover:shadow-lg",
                    location.pathname === '/team/codee-jessen' 
                      ? "bg-gradient-to-r from-action-primary to-action-urgent text-white border-action-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-trust-primary rounded-full mr-3"></span>
                    Codee Jessen - Master Plumber
                  </span>
                </a>
                <a 
                  href="/team/michelle-jessen" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-action-primary hover:to-action-urgent hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-action-primary/20 hover:shadow-lg",
                    location.pathname === '/team/michelle-jessen' 
                      ? "bg-gradient-to-r from-action-primary to-action-urgent text-white border-action-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-success-primary rounded-full mr-3"></span>
                    Michelle Jessen - Owner
                  </span>
                </a>
                <a 
                  href="/team/zack" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-action-primary hover:to-action-urgent hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-action-primary/20 hover:shadow-lg",
                    location.pathname === '/team/zack' 
                      ? "bg-gradient-to-r from-action-primary to-action-urgent text-white border-action-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-action-warm rounded-full mr-3"></span>
                    Zack - Newest Team Member
                  </span>
                </a>
                <a 
                  href="/team/jesse-sellers" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-action-primary hover:to-action-urgent hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-action-primary/20 hover:shadow-lg",
                    location.pathname === '/team/jesse-sellers' 
                      ? "bg-gradient-to-r from-action-primary to-action-urgent text-white border-action-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-trust-primary rounded-full mr-3"></span>
                    Jesse Sellers - Technician
                  </span>
                </a>
              </div>
            )}
          </div>
          
          {/* Pricing Dropdown - Highlighted for Transparent Pricing */}
          <div className="relative">
            <button
              onClick={() => setIsPricingOpen(!isPricingOpen)}
              onMouseEnter={handlePricingMouseEnter}
              onMouseLeave={handlePricingMouseLeave}
              className={cn(
                "flex items-center px-3 lg:px-4 py-2 rounded-lg bg-gradient-to-r text-white transition-all duration-300 text-sm font-bold shadow-md hover:shadow-xl border transform hover:scale-[1.02] ring-2 ring-success-primary/30 animate-pulse",
                isOnPricingPage() 
                  ? "from-success-primary to-success-eco border-success-primary hover:from-success-eco hover:to-green-600 hover:border-success-eco" 
                  : "from-success-primary to-success-eco border-success-primary hover:from-success-eco hover:to-green-600 hover:border-success-eco"
              )}
            >
              Pricing
              <ChevronDown size={16} className="ml-1" />
            </button>
            
            {isPricingOpen && (
              <div 
                className="absolute top-full left-0 mt-0 bg-gradient-to-br from-neutral-white to-success-light/30 rounded-xl shadow-2xl border border-success-primary/20 py-3 min-w-64 z-50 backdrop-blur-sm"
                onMouseEnter={handlePricingDropdownEnter}
                onMouseLeave={handlePricingDropdownLeave}
                onClick={handleLinkClick}
              >
                <a 
                  href="/upfront-plumbing-prices-denver" 
                  className="block px-6 py-3 text-sm font-semibold text-authority-navy hover:bg-gradient-to-r hover:from-success-primary hover:to-success-eco hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-success-primary/20 hover:shadow-lg"
                  onClick={handleLinkClick}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-success-primary rounded-full mr-3"></span>
                    Pricing Philosophy
                  </span>
                </a>
                <a 
                  href="/flat-rate-affordable-rheem-water-heaters-in-colorado" 
                  className={cn(
                    "block px-6 py-3 text-sm font-bold hover:bg-gradient-to-r hover:from-action-urgent hover:to-red-700 hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-action-urgent/20 hover:shadow-lg bg-gradient-to-r from-action-urgent/10 to-orange-500/10 border-action-urgent/20",
                    location.pathname === '/flat-rate-affordable-rheem-water-heaters-in-colorado' 
                      ? "bg-gradient-to-r from-action-urgent to-red-700 text-white border-action-urgent/20 shadow-lg" 
                      : "text-action-urgent"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-action-urgent rounded-full mr-3 animate-pulse"></span>
                    💰 $2000-$2100 Water Heater Deal
                  </span>
                </a>
                <div className="border-t border-success-primary/10 my-2 mx-4"></div>
                <a 
                  href="/diagnostic-services-pricing" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-success-primary hover:to-success-eco hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-success-primary/20 hover:shadow-lg",
                    location.pathname === '/diagnostic-services-pricing' 
                      ? "bg-gradient-to-r from-success-primary to-success-eco text-white border-success-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-action-warm rounded-full mr-3"></span>
                    Diagnostic Services
                  </span>
                </a>
                <a 
                  href="/water-heater-pricing" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-success-primary hover:to-success-eco hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-success-primary/20 hover:shadow-lg",
                    location.pathname === '/water-heater-pricing' 
                      ? "bg-gradient-to-r from-success-primary to-success-eco text-white border-success-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-trust-primary rounded-full mr-3"></span>
                    Water Heaters
                  </span>
                </a>
                <a 
                  href="/tankless-water-heater-pricing" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-success-primary hover:to-success-eco hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-success-primary/20 hover:shadow-lg",
                    location.pathname === '/tankless-water-heater-pricing' 
                      ? "bg-gradient-to-r from-success-primary to-success-eco text-white border-success-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-action-primary rounded-full mr-3"></span>
                    Tankless Systems
                  </span>
                </a>
                <a 
                  href="/sewer-cleaning-pricing" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-success-primary hover:to-success-eco hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-success-primary/20 hover:shadow-lg",
                    location.pathname === '/sewer-cleaning-pricing' 
                      ? "bg-gradient-to-r from-success-primary to-success-eco text-white border-success-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-action-urgent rounded-full mr-3"></span>
                    Sewer Cleaning
                  </span>
                </a>
                <a 
                  href="/sewer-replacement-spot-repair-pricing" 
                  className={cn(
                    "block px-6 py-3 text-sm font-medium hover:bg-gradient-to-r hover:from-success-primary hover:to-success-eco hover:text-white transition-all duration-200 rounded-lg mx-2 border border-transparent hover:border-success-primary/20 hover:shadow-lg",
                    location.pathname === '/sewer-replacement-spot-repair-pricing' 
                      ? "bg-gradient-to-r from-success-primary to-success-eco text-white border-success-primary/20 shadow-lg" 
                      : "text-authority-navy"
                  )}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-red-600 rounded-full mr-3"></span>
                    Sewer Replacement & Repair
                  </span>
                </a>
                <div className="border-t border-success-primary/10 my-2 mx-4"></div>
                <a 
                  href="/pricing" 
                  className="block px-6 py-3 text-sm font-medium text-neutral-gray hover:bg-gradient-to-r hover:from-neutral-light hover:to-neutral-white hover:text-authority-navy transition-all duration-200 rounded-lg mx-2 border border-transparent"
                  onClick={handleLinkClick}
                >
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-neutral-gray rounded-full mr-3"></span>
                    More Services
                  </span>
                </a>
              </div>
            )}
          </div>
          
        </nav>
        
        <button 
          className="md:hidden text-neutral-white hover:text-action-warm transition-colors"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      
      {isMenuOpen && (
        <nav className="md:hidden absolute top-full left-0 w-full bg-gradient-to-br from-trust-deep/98 to-authority-navy/98 backdrop-blur-lg shadow-2xl border-t border-trust-primary/20 max-h-[calc(100vh-160px)] overflow-y-auto animate-slide-in-right">
          <div className="container-custom flex flex-col space-y-4 py-6">
            <a 
              href="/" 
              className="text-neutral-white hover:text-action-warm transition-colors py-2 px-4 rounded-lg hover:bg-white/10"
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </a>
            
            {/* Services Mobile Section */}
            <div className="py-2">
              <div className="text-trust-light text-xs font-medium mb-3 uppercase tracking-wide px-4">Services</div>
              <a 
                href="/residential-plumbing" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Residential Plumbing
              </a>
              <a 
                href="/commercial-plumbing" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Commercial Plumbing
              </a>
              <a 
                href="/sewer-repair-replacement" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Sewer Repair & Replacement
              </a>
              <a 
                href="/bathroom-kitchen-remodel" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Bathroom & Kitchen Remodel
              </a>
              <a
                href="/custom-home-plumbing"
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Custom Home Plumbing
              </a>
            </div>
            
            {/* Service Areas Mobile Section */}
            <div className="py-2">
              <div className="text-trust-light text-xs font-medium mb-3 uppercase tracking-wide px-4">Service Areas</div>
              <a 
                href="/highlands-ranch-plumber" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Highlands Ranch Plumber
              </a>
              <a 
                href="/littleton-plumber" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Littleton Plumber
              </a>
              <a 
                href="/lakewood-plumber" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Lakewood Plumber
              </a>
              <a 
                href="/longmont-plumber" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Longmont Plumber
              </a>
              <a 
                href="/thornton-plumber" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Thornton Plumber
              </a>
              <a 
                href="/denver-plumber" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Denver Plumber
              </a>
              <a 
                href="/arvada-plumber" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Arvada Plumber
              </a>
              <a 
                href="/castle-rock-plumber" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Castle Rock Plumber
              </a>
              <a 
                href="/centennial-plumber" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Centennial Plumber
              </a>
            </div>
            
            <a 
              href="/reviews" 
              className="text-neutral-white hover:text-action-warm transition-colors py-2 px-4 rounded-lg hover:bg-white/10"
              onClick={() => setIsMenuOpen(false)}
            >
              Reviews
            </a>
            
            {/* About Mobile Section */}
            <div className="py-2">
              <div className="text-trust-light text-xs font-medium mb-3 uppercase tracking-wide px-4">About</div>
              <a 
                href="/meet-the-team" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Meet the Team
              </a>
              <a
                href="/team/codee-jessen"
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Codee Jessen - Master Plumber
              </a>
              <a 
                href="/team/michelle-jessen" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Michelle Jessen - Owner
              </a>
              <a
                href="/team/zack" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Zack - Newest Team Member
              </a>
              <a 
                href="/team/jesse-sellers" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Jesse Sellers - Technician
              </a>
            </div>
            
            {/* Pricing Mobile Section - Transparent Pricing Highlight */}
            <div className="py-2">
              <div className="text-success-eco text-xs font-bold mb-3 uppercase tracking-wide px-4 flex items-center">
                Pricing
              </div>
              <a 
                href="/upfront-plumbing-prices-denver" 
                className="text-neutral-white hover:text-success-eco transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-success-primary/20 font-semibold"
                onClick={() => setIsMenuOpen(false)}
              >
                💎 Pricing Philosophy
              </a>
              <a 
                href="/flat-rate-affordable-rheem-water-heaters-in-colorado" 
                className="text-action-urgent hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10 font-bold"
                onClick={() => setIsMenuOpen(false)}
              >
                💰 $2000-$2100 Water Heater Deal
              </a>
              <a 
                href="/diagnostic-services-pricing" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Diagnostic Services
              </a>
              <a 
                href="/water-heater-pricing" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Water Heaters
              </a>
              <a 
                href="/tankless-water-heater-pricing" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Tankless Systems
              </a>
              <a 
                href="/sewer-cleaning-pricing" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Sewer Cleaning
              </a>
              <a 
                href="/sewer-replacement-spot-repair-pricing" 
                className="text-neutral-white hover:text-action-warm transition-colors py-2 pl-6 pr-4 block rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Sewer Replacement & Repair
              </a>
            </div>
            
          </div>
        </nav>
      )}
    </header>
    </>
  );
};

export default Header;
